// Profile (UC-02/03): view and edit basic profile + basketball preferences.
// Also hosts demo controls (switch account / reset world).

import $ from 'jquery';
import type { GameFormat, ProfileVisibility, SkillLevel } from '@shared/models';
import type { Screen } from './types';
import { api } from '../api';
import { getCurrentUser, setCurrentUser, signOut } from '../session';
import { navigate } from '../router';
import { avatarHtml, esc } from '../ui/dom';
import { FORMAT_TR, SKILL_TR } from '../ui/format';
import { toast } from '../ui/toast';
import { confirmDialog } from '../ui/confirm';

const FORMATS: GameFormat[] = ['3v3', '4v4', '5v5', 'pickup'];
const SKILLS: SkillLevel[] = ['beginner', 'intermediate', 'advanced'];
const AREAS = ['Çankaya', 'Kızılay', 'Bahçelievler', 'Çayyolu', 'Keçiören', 'ODTÜ', 'Bilkent'];

// Discoverability options, with plain-language Turkish labels + help text.
const VISIBILITIES: Array<{ value: ProfileVisibility; label: string; help: string }> = [
  { value: 'private', label: 'Gizli', help: 'Kimse seni aramayla ya da öneriyle bulamaz; sadece davet kodunla eklenebilirsin.' },
  { value: 'fof', label: 'Arkadaşların arkadaşları', help: 'Ortak arkadaşın olan kişiler seni önerilerde görebilir.' },
  { value: 'public', label: 'Herkese açık', help: 'Herkes seni isimle arayıp bulabilir.' },
];

export const ProfileScreen: Screen = async (body) => {
  const me = getCurrentUser();
  if (!me) return;

  const areaOpts = AREAS.map(
    (a) => `<option value="${esc(a)}" ${me.homeArea === a ? 'selected' : ''}>${esc(a)}</option>`,
  ).join('');
  const skillOpts = SKILLS.map(
    (s) => `<option value="${s}" ${me.skill === s ? 'selected' : ''}>${SKILL_TR[s]}</option>`,
  ).join('');
  const formatChips = FORMATS.map(
    (f) => `<button class="chip ${me.preferredFormats.includes(f) ? 'is-on' : ''}" data-format="${f}">${FORMAT_TR[f]}</button>`,
  ).join('');
  const visibilityOpts = VISIBILITIES.map(
    (v) => `<option value="${v.value}" ${me.visibility === v.value ? 'selected' : ''}>${esc(v.label)}</option>`,
  ).join('');
  const visibilityHelp = (v: ProfileVisibility): string =>
    VISIBILITIES.find((o) => o.value === v)?.help ?? '';

  body.html(`
    <div class="profile">
      <div class="profile__head">
        ${avatarHtml(me.name, me.avatarUrl, 72)}
        <div class="profile__name">${esc(me.name)}</div>
        <div class="profile__area">${esc(me.homeArea)} · ${SKILL_TR[me.skill]}</div>
      </div>

      <label class="field">
        <span class="field__label">İsim</span>
        <input class="input" id="p-name" value="${esc(me.name)}" />
      </label>

      <label class="field">
        <span class="field__label">Bölge (konum)</span>
        <select class="select" id="p-area">${areaOpts}</select>
      </label>

      <label class="field">
        <span class="field__label">Seviye</span>
        <select class="select" id="p-skill">${skillOpts}</select>
      </label>

      <div class="field">
        <span class="field__label">Tercih edilen format</span>
        <div class="chips" id="p-formats">${formatChips}</div>
      </div>

      <div class="section-title">Gizlilik</div>
      <label class="field">
        <span class="field__label">Beni kimler bulabilir?</span>
        <select class="select" id="p-visibility">${visibilityOpts}</select>
        <span class="muted" id="p-visibility-help">${esc(visibilityHelp(me.visibility))}</span>
      </label>
      <div class="field--row">
        <p class="muted" style="margin:0">Davet kodun: <span class="invite-code" id="p-code">${esc(me.inviteCode)}</span></p>
        <button type="button" class="btn btn--ghost btn--sm" id="p-code-renew">Yenile</button>
      </div>
      <p class="muted">Gizli olsan bile bu kodu paylaştığın kişiler sana istek gönderebilir. Kodun herkese açık olduysa yenileyerek eskisini geçersiz kılabilirsin.</p>

      <button class="btn btn--primary btn--block" id="p-save">Kaydet</button>

      <div class="section-title">Demo</div>
      <button class="btn btn--ghost btn--block" id="p-reset">Demo dünyasını sıfırla</button>
      <button class="btn btn--ghost btn--block" id="p-switch">Hesap değiştir / çıkış</button>
    </div>
  `);

  const selected = new Set<GameFormat>(me.preferredFormats);
  body.on('click', '#p-formats .chip', function () {
    const f = $(this).data('format') as GameFormat;
    if (selected.has(f)) selected.delete(f);
    else selected.add(f);
    $(this).toggleClass('is-on');
  });

  body.on('change', '#p-visibility', function () {
    const v = $(this).val() as ProfileVisibility;
    $('#p-visibility-help').text(visibilityHelp(v));
  });

  $('#p-save').on('click', async () => {
    const updated = await api.call('players.update', {
      name: ($('#p-name').val() as string).trim() || me.name,
      homeArea: $('#p-area').val() as string,
      skill: $('#p-skill').val() as SkillLevel,
      preferredFormats: [...selected],
      visibility: $('#p-visibility').val() as ProfileVisibility,
    });
    setCurrentUser(updated);
    toast('Profil kaydedildi', 'success');
  });

  $('#p-code-renew').on('click', async () => {
    const ok = await confirmDialog({
      title: 'Davet kodunu yenile',
      message: 'Yeni bir davet kodu oluşturulacak ve eski kodun geçersiz olacak. Eski kodu paylaştığın kişiler artık onu kullanamaz.',
      confirmLabel: 'Yenile',
      danger: false,
    });
    if (!ok) return;
    try {
      const updated = await api.call('players.renewInviteCode', undefined);
      setCurrentUser(updated);
      $('#p-code').text(updated.inviteCode);
      toast('Davet kodun yenilendi', 'success');
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err), 'error');
    }
  });

  $('#p-reset').on('click', async () => {
    await api.call('demo.reset', undefined);
    toast('Demo sıfırlandı', 'info');
    navigate('/home');
  });

  $('#p-switch').on('click', async () => {
    await signOut();
    navigate('/signin');
  });
};
